import { isMobileWeb } from '@toss/utils';
import html2canvas from 'html2canvas';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks';
import { DesktopQRCodeSection } from './DesktopQRCodeSection';
import { EnterCodeSection } from './EnterCodeSection';
import { LinkCopySection } from './LinkCopySection';
import { MobileQRCodeSection } from './MobileQRCodeSection';
interface ShareContentProps {
  title: string;
  enterCode: string | number;
  shareUrl: string;
}
export function ShareContent({
  enterCode,
  shareUrl,
  title
}: ShareContentProps) {
  const {
    t
  } = useTranslation(['common']);
  const {
    isMobile
  } = useViewport();
  const isMobileOrTablet = isMobileWeb() || isMobile;
  const handleCanvasError = (message: string, error: unknown) => {
    console.error(message, error);
    alert(`${t('googleClassroom.unknownErrorDialog.title')}\n${t('googleClassroom.unknownErrorDialog.content')}`);
  };
  const createQRCodeCanvas = (qrCode: HTMLDivElement) => html2canvas(qrCode, {
    backgroundColor: 'white',
    scale: 2
  });
  const handleCopyQRImage = async (qrCode: HTMLDivElement, onSuccess?: () => void) => {
    try {
      const clipboardItem = new ClipboardItem({
        'image/png': (async () => {
          const canvas = await createQRCodeCanvas(qrCode);
          return new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            }, 'image/png');
          });
        })()
      });
      await navigator.clipboard.write([clipboardItem]);
      onSuccess?.();
    } catch (error) {
      handleCanvasError('Failed to copy QR code image', error);
    }
  };
  const handleDownloadQRCode = async (qrCode: HTMLDivElement) => {
    try {
      const canvas = await createQRCodeCanvas(qrCode);
      const link = document.createElement('a');
      link.download = `qr-code-${title}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      handleCanvasError('Failed to download QR code', error);
    }
  };
  return <div className="flex min-h-0 w-full flex-1 flex-col gap-md mobile-lg:flex-none" data-sentry-component="ShareContent" data-sentry-source-file="ShareContent.tsx">
      <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-xs rounded-md bg-neutral px-md py-lg max-mobile-lg:overflow-hidden mobile-lg:flex-none">
        <EnterCodeSection enterCode={enterCode} data-sentry-element="EnterCodeSection" data-sentry-source-file="ShareContent.tsx" />
        {isMobileOrTablet ? <MobileQRCodeSection url={shareUrl} onDownload={handleDownloadQRCode} onCopy={handleCopyQRImage} /> : <DesktopQRCodeSection url={shareUrl} onDownload={handleDownloadQRCode} onCopy={handleCopyQRImage} />}
      </div>
      <LinkCopySection url={shareUrl} data-sentry-element="LinkCopySection" data-sentry-source-file="ShareContent.tsx" />
    </div>;
}